import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../../../firebase";

import { Text } from "../../../../design/text";
import { Professional } from "../../../../models/Professional";

// Step components
import OnboardingSelectRolesStep from "./components/OnboardingSelectRolesStep";
import OnboardingPersonalInfoStep from "./components/OnboardingPersonalInfoStep";
import OnboardingLocationAndLicensingStep from "./components/OnboardingLocationAndLicensingStep";
import OnboardingLanguagesSpokenStep from "./components/OnboardingLanguagesSpokenStep";
import OnboardingBioAndAvatarStep from "./components/OnboardingBioAndAvatarStep";
import OnboardingReviewAndSubscribeStep from "./components/OnboardingReviewAndSubscribeStep";
import { useUser } from "../../../../contexts/UserContext";
import { professionalProducts } from "../../../../constants/professionalProducts";
import { CheckoutService } from "../../../../services/CheckoutService";

const OnboardingProfessional = () => {
  const navigate = useNavigate();
  const { currentUser, createProfessional } = useUser();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(parseInt(searchParams.get("step") || "0"));
  const [formData, setFormData] = useState<Partial<Professional>>({
    languagesSpoken: ["English"],
    offersVirtualRoles: true,
  });
  const [isSaving, setIsSaving] = useState(false);

  const nextStep = () => setStep((prev) => prev + 1);
  const previousStep = () => setStep((prev) => prev - 1);

  const onLogoutButtonPressed = () => {
    signOut(auth);
    navigate("/");
  };

  const onSubscribe = async () => {
    setIsSaving(true);
    try {
      await createProfessional(currentUser!._id, null, formData);
      const professionalProduct =
        professionalProducts["professional_marketplace"];
      const url =
        await CheckoutService.checkoutProfessionalProduct(professionalProduct);
      window.location.href = url!;
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="absolute flex items-center justify-between w-full max-w-screen-md p-4">
        <Text
          onClick={onLogoutButtonPressed}
          className="cursor-pointer text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
        >
          Logout
        </Text>
      </div>

      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="w-full max-w-screen-md">
          {step === 0 && (
            <OnboardingSelectRolesStep
              formData={formData}
              setFormData={setFormData}
              onStepComplete={nextStep}
              onBack={previousStep}
            />
          )}
          {step === 1 && (
            <OnboardingPersonalInfoStep
              formData={formData}
              setFormData={setFormData}
              onStepComplete={nextStep}
              onBack={previousStep}
            />
          )}
          {step === 2 && (
            <OnboardingLocationAndLicensingStep
              formData={formData}
              setFormData={setFormData}
              onStepComplete={nextStep}
              onBack={previousStep}
            />
          )}
          {step === 3 && (
            <OnboardingLanguagesSpokenStep
              formData={formData}
              setFormData={setFormData}
              onStepComplete={nextStep}
              onBack={previousStep}
            />
          )}
          {step === 4 && (
            <OnboardingBioAndAvatarStep
              formData={formData}
              setFormData={setFormData}
              onStepComplete={nextStep}
              onBack={previousStep}
            />
          )}
          {step === 5 && (
            <OnboardingReviewAndSubscribeStep
              formData={formData}
              onBack={previousStep}
              onSubscribe={onSubscribe}
              isLoading={isSaving}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default OnboardingProfessional;
